import React, { useState, useEffect } from 'react';
import AdminHeader from './AdminHeader';
import PageHeader from './PageHeader';
import './CategoryManager.scss';

interface Category {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const API_BASE = 'http://localhost:4000/api';

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      setError('Unable to connect to backend. Please ensure the server is running.');
      console.error('Fetch categories error:', err);
      // Use mock data for demo if backend is not available
      setCategories([
        { id: 1, name: 'Web Development', createdAt: '2025-10-15', updatedAt: '2025-10-15' },
        { id: 2, name: 'Mobile Apps', createdAt: '2025-10-15', updatedAt: '2025-10-15' },
        { id: 3, name: 'SEO & Marketing', createdAt: '2025-10-15', updatedAt: '2025-10-15' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Create new category
  const createCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() }),
      });
      if (!response.ok) throw new Error('Failed to create category');
      const newCategory = await response.json();
      setCategories([...categories, newCategory]);
      setNewCategoryName('');
    } catch (err) {
      setError('Unable to create category. Backend connection failed.');
      console.error('Create category error:', err);
      // Mock create for demo
      const mockCategory = {
        id: Date.now(),
        name: newCategoryName.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setCategories([...categories, mockCategory]);
      setNewCategoryName('');
    } finally {
      setLoading(false);
    }
  };

  // Delete category
  const deleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/categories/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete category');
      setCategories(categories.filter(cat => cat.id !== id));
    } catch (err) {
      setError('Unable to delete category. Backend connection failed.');
      console.error('Delete category error:', err);
      // Mock delete for demo
      setCategories(categories.filter(cat => cat.id !== id));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <div className="admin-dashboard">
      <AdminHeader />
      <div className="category-manager">
        <PageHeader 
          title="Category Management" 
          subtitle="Manage blog categories for better organization"
        />

        {error && (
          <div className="category-manager__error">
            <strong>⚠️ {error}</strong>
            <p>Using demo data for now. Start the backend server to connect to the database.</p>
          </div>
        )}

        <div className="category-manager__create">
          <div className="category-manager__create-form">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Enter category name (e.g., Technology, Business)"
              className="category-manager__input"
              onKeyPress={(e) => e.key === 'Enter' && createCategory()}
            />
            <button
              onClick={createCategory}
              disabled={loading || !newCategoryName.trim()}
              className="category-manager__btn category-manager__btn--primary"
            >
              {loading ? 'Creating...' : 'Add Category'}
            </button>
          </div>
        </div>

        <div className="category-manager__list">
          <h3>Existing Categories ({categories.length})</h3>
          {loading && categories.length === 0 ? (
            <div className="category-manager__loading">Loading categories...</div>
          ) : categories.length === 0 ? (
            <div className="category-manager__empty">
              No categories yet. Create your first category above.
            </div>
          ) : (
            <div className="category-manager__grid">
              {categories.map((category) => (
                <div key={category.id} className="category-manager__card">
                  <div className="category-manager__card-content">
                    <h4 className="category-manager__card-name">{category.name}</h4>
                    <p className="category-manager__card-date">
                      Created: {new Date(category.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="category-manager__card-actions">
                    <button
                      onClick={() => deleteCategory(category.id)}
                      className="category-manager__btn category-manager__btn--danger"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
